// ─── ConflictResolutionPanel — the actions half of a sync conflict ───────────
//
// Shared by the POS Sync page and the admin Sync Health page
// (docs/sync-conflict-resolution-plan.md Phase 6). Shows only the outcomes the
// API offers for this conflict (`allowed_resolutions`), names them for what
// they do, requires a note for a merge or a discard (US-SYNC-003), shows why a
// refused offline create was refused, and lets the resolver confirm a merged
// record field by field before it is applied.

import { useMemo, useState } from 'react';
import { Alert } from '../../BasicComponents/Alert';
import { Button } from '../../BasicComponents/Button';
import { TextArea } from '../../BasicComponents/TextArea';
import { cn } from '../../../constants/design-tokens';

export type ConflictResolutionChoice = 'CLIENT_WINS' | 'SERVER_WINS' | 'MERGED' | 'DISCARDED';

const ALL_RESOLUTIONS: ConflictResolutionChoice[] = ['CLIENT_WINS', 'SERVER_WINS', 'MERGED', 'DISCARDED'];

export interface ConflictResolutionPanelProps {
  entityType: string;
  /** VERSION (offline edit vs newer server copy) or REJECTED_CREATE (server refused an offline create). */
  kind?: string;
  /** From the conflict detail. Absent (older API) → all four outcomes. */
  allowedResolutions?: string[];
  clientVersion: Record<string, unknown>;
  serverVersion: Record<string, unknown>;
  mergedSuggestion?: Record<string, unknown> | null;
  /** Why the last attempt to apply a resolution failed (the conflict went back to pending). */
  resolutionError?: string | null;
  note: string;
  onNoteChange: (value: string) => void;
  busy: boolean;
  busyAs?: string | null;
  /** True when this user may not resolve this conflict (e.g. a manager on a bill). */
  blocked?: boolean;
  onResolve: (resolution: ConflictResolutionChoice, mergedVersion?: Record<string, unknown>) => void;
  /** Discard goes through the app's own confirm dialog. */
  onDiscard: () => void;
}

/** A customer edit's payload is `{ base, changes }`; the merge editor reads the changed fields from it. */
function tillChanges(clientVersion: Record<string, unknown>): Record<string, unknown> {
  const changes = clientVersion.changes;
  return changes && typeof changes === 'object' ? (changes as Record<string, unknown>) : clientVersion;
}

function label(choice: ConflictResolutionChoice, entityType: string, kind?: string): string {
  if (choice === 'CLIENT_WINS') {
    if (kind === 'REJECTED_CREATE') return entityType === 'BILL' ? 'Accept as sold' : 'Accept and retry';
    return "Use the till's version";
  }
  if (choice === 'SERVER_WINS') return 'Keep the server version';
  if (choice === 'MERGED') return 'Merge field by field…';
  return 'Discard the till’s change';
}

function asText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

/** Text back to the shape of the original value (tags stay an array). */
function fromText(text: string, like: unknown): unknown {
  if (Array.isArray(like)) return text.split(',').map((t) => t.trim()).filter(Boolean);
  return text === '' ? null : text;
}

export function ConflictResolutionPanel({
  entityType,
  kind,
  allowedResolutions,
  clientVersion,
  serverVersion,
  mergedSuggestion,
  resolutionError,
  note,
  onNoteChange,
  busy,
  busyAs,
  blocked = false,
  onResolve,
  onDiscard,
}: ConflictResolutionPanelProps) {
  const allowed = (allowedResolutions ?? ALL_RESOLUTIONS).filter(
    (r): r is ConflictResolutionChoice => (ALL_RESOLUTIONS as string[]).includes(r),
  );
  const noteRequired = allowed.includes('MERGED') || allowed.includes('DISCARDED');
  const noteMissing = note.trim().length === 0;
  const [merging, setMerging] = useState(false);
  const changes = useMemo(() => tillChanges(clientVersion), [clientVersion]);
  const fields = useMemo(
    () => Object.keys(mergedSuggestion ?? changes),
    [mergedSuggestion, changes],
  );
  const [merged, setMerged] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f, asText((mergedSuggestion ?? changes)[f])])),
  );
  const refusedReason = kind === 'REJECTED_CREATE' ? asText(serverVersion.reason) : '';

  const confirmMerge = () => {
    const record: Record<string, unknown> = {};
    for (const f of fields) {
      record[f] = fromText(merged[f] ?? '', (mergedSuggestion ?? changes)[f] ?? serverVersion[f]);
    }
    onResolve('MERGED', record);
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {refusedReason && (
        <div className="rounded-xl bg-gradient-to-r from-warning-500/10 to-danger-500/10 ring-1 ring-warning-500/30 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-warning-700 dark:text-warning-300">
            Refused by the server
          </p>
          <p className="mt-1 text-sm font-medium text-neutral-900 dark:text-white">{refusedReason}</p>
          {entityType === 'BILL' && (
            <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
              This sale was paid at the till. Accepting records it exactly as sold — the till’s prices, even if
              stock goes below zero. Correct a wrong sale afterwards with a return or void.
            </p>
          )}
        </div>
      )}

      {resolutionError && (
        <Alert
          variant="danger"
          title="The last resolution could not be applied"
          description={resolutionError}
        />
      )}

      {blocked && (
        <Alert
          variant="warning"
          title="Owner sign-off required"
          description="Financial conflicts (BILL, EXPENSE) must be resolved by the Owner or above. Please escalate to your store Owner."
        />
      )}

      <TextArea
        label={noteRequired ? 'Note (required to merge or discard)' : 'Note (optional)'}
        placeholder="Why this resolution?"
        value={note}
        onChange={onNoteChange}
        rows={2}
        maxLength={500}
      />

      {merging && (
        <div className="rounded-xl ring-1 ring-violet-500/30 bg-gradient-to-br from-violet-500/5 to-pink-500/5 p-4 space-y-3">
          <p className="text-sm font-semibold text-neutral-900 dark:text-white">Confirm the merged record</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 dark:text-neutral-400">
                  <th className="py-1 pr-3 font-medium">Field</th>
                  <th className="py-1 pr-3 font-medium">Server</th>
                  <th className="py-1 pr-3 font-medium">Till</th>
                  <th className="py-1 font-medium">Merged</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((f) => {
                  const serverText = asText(serverVersion[f]);
                  const tillText = f in changes ? asText(changes[f]) : '';
                  return (
                    <tr key={f} className="border-t border-neutral-100 dark:border-neutral-700 even:bg-neutral-50/60 dark:even:bg-neutral-800/40">
                      <td className="py-2 pr-3 font-medium text-neutral-700 dark:text-neutral-300">{f.replace(/_/g, ' ')}</td>
                      <td className="py-2 pr-3">
                        <button
                          type="button"
                          className={cn('rounded-md px-2 py-1 text-left transition-all duration-200 hover:bg-brand-50 dark:hover:bg-brand-900/30', merged[f] === serverText && 'ring-1 ring-brand-500/40')}
                          onClick={() => setMerged((m) => ({ ...m, [f]: serverText }))}
                          aria-label={`Use the server value for ${f}`}
                        >
                          {serverText || '—'}
                        </button>
                      </td>
                      <td className="py-2 pr-3">
                        {f in changes ? (
                          <button
                            type="button"
                            className={cn('rounded-md px-2 py-1 text-left transition-all duration-200 hover:bg-violet-50 dark:hover:bg-violet-900/30', merged[f] === tillText && 'ring-1 ring-violet-500/40')}
                            onClick={() => setMerged((m) => ({ ...m, [f]: tillText }))}
                            aria-label={`Use the till value for ${f}`}
                          >
                            {tillText || '—'}
                          </button>
                        ) : (
                          <span className="text-neutral-400">unchanged</span>
                        )}
                      </td>
                      <td className="py-2">
                        <input
                          aria-label={`Merged ${f}`}
                          className="w-full rounded-md border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 px-2 py-1 text-sm text-neutral-900 dark:text-white"
                          value={merged[f] ?? ''}
                          onChange={(e) => setMerged((m) => ({ ...m, [f]: e.target.value }))}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="primary"
              onClick={confirmMerge}
              disabled={busy || blocked || noteMissing}
              loading={busyAs === 'MERGED'}
            >
              Apply merged record
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMerging(false)} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {!merging && (
        <div className="flex flex-wrap gap-2">
          {allowed.map((choice) => {
            const needsNote = choice === 'MERGED' || choice === 'DISCARDED';
            const onClick = choice === 'MERGED'
              ? () => setMerging(true)
              : choice === 'DISCARDED'
                ? onDiscard
                : () => onResolve(choice);
            return (
              <Button
                key={choice}
                size="sm"
                variant={choice === 'CLIENT_WINS' ? 'primary' : choice === 'DISCARDED' ? 'danger' : 'secondary'}
                onClick={onClick}
                disabled={busy || blocked || (choice === 'DISCARDED' && noteMissing)}
                loading={busyAs === choice}
                title={needsNote && noteMissing && choice === 'DISCARDED' ? 'Add a note to discard' : undefined}
              >
                {label(choice, entityType, kind)}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}
